/**
 * manager-product.js
 * Gerencia quick-add ao carrinho nos cards de produto.
 * Disponível como window.ProductManager.
 *
 * Dependências (devem carregar antes):
 *   - window.CartManager    (manager-cart.js)
 *   - window.ButtonFeedback (manager-global-button-feedback.js)
 *
 * ─── HTML esperado (.productCard) ─────────────────────────────────────────
 *
 *   <article class="productCard">
 *
 *     <!-- Botão que abre/fecha o popup de tamanhos -->
 *     <button data-product-add-toggle aria-expanded="false">
 *       Adicionar
 *     </button>
 *
 *     <!-- Popup de tamanhos -->
 *     <div data-size-popup hidden>
 *       <button class="productCard__sizeOption"
 *               data-size-option
 *               data-variant-id="12345678">P</button>
 *       <button class="productCard__sizeOption"
 *               data-size-option
 *               data-variant-id="12345679">M</button>
 *     </div>
 *
 *   </article>
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   .productCard               → container do card (escopo do popup)
 *   data-product-add-toggle    → abre/fecha popup
 *   data-size-popup            → container do popup
 *   data-size-option           → opção de tamanho (lê data-variant-id)
 *   data-variant-id            → ID da variante a adicionar
 *
 * ─── Comportamento ────────────────────────────────────────────────────────
 *
 *   - Click em data-product-add-toggle → abre popup do card / fecha outros
 *   - Click em data-size-option → CartManager.add() + fecha popup
 *   - Click fora de qualquer popup → fecha todos
 *   - Funciona com cards dinâmicos (event delegation em document)
 */

class ProductManager {
  constructor() {
    this._onClick = this._onClick.bind(this);
    document.addEventListener('click', this._onClick);
  }

  // ─── Event delegation ─────────────────────────────────────────────────────

  _onClick(e) {
    // Toggle popup
    const toggleBtn = e.target.closest('[data-product-add-toggle]');
    if (toggleBtn) {
      e.stopPropagation();
      const card   = toggleBtn.closest('.productCard');
      if (!card) return;
      const isOpen = card.querySelector('[data-size-popup]')?.classList.contains('is-open');
      this._closeAllPopups();
      if (!isOpen) this._openPopup(card);
      return;
    }

    // Selecionar tamanho → add to cart
    const selectedOption = e.target.closest('[data-product-add-selected]');
    if (selectedOption) {
      const variantId = this._resolveSelectedVariant(selectedOption);
      if (variantId) this._addToCart(selectedOption, variantId);
      return;
    }

    // Selecionar cor (swatch) no card
    const colorSwatch = e.target.closest('[data-color-swatch]');
    if (colorSwatch) {
      const card = colorSwatch.closest('.productCard');
      if (card) {
        card.querySelectorAll('[data-color-swatch]').forEach(s => s.removeAttribute('data-selected'));
        colorSwatch.setAttribute('data-selected', 'true');
      }
      return;
    }

    const sizeOption = e.target.closest('[data-size-option]');
    if (sizeOption) {
      const variantId = this._resolveColorVariant(sizeOption) || sizeOption.dataset.variantId;
      if (variantId) this._addToCart(sizeOption, variantId);
      return;
    }

    // Fechar ao clicar fora
    if (!e.target.closest('[data-size-popup]') && !e.target.closest('[data-product-add-toggle]')) {
      this._closeAllPopups();
    }
  }

  // ─── Popup ────────────────────────────────────────────────────────────────

  _openPopup(card) {
    const popup     = card.querySelector('[data-size-popup]');
    const toggleBtn = card.querySelector('[data-product-add-toggle]');
    if (!popup) return;
    popup.classList.add('is-open');
    popup.removeAttribute('hidden');
    toggleBtn?.setAttribute('aria-expanded', 'true');
  }

  _closePopup(card) {
    const popup     = card.querySelector('[data-size-popup]');
    const toggleBtn = card.querySelector('[data-product-add-toggle]');
    if (!popup) return;
    popup.classList.remove('is-open');
    popup.setAttribute('hidden', '');
    toggleBtn?.setAttribute('aria-expanded', 'false');
  }

  _closeAllPopups() {
    document.querySelectorAll('.productCard [data-size-popup].is-open').forEach(popup => {
      const card = popup.closest('.productCard');
      if (card) this._closePopup(card);
    });
  }

  // ─── Add to cart ──────────────────────────────────────────────────────────

  _resolveSelectedVariant(btn) {
    const card = btn.closest('.productCard');
    if (!card) return btn.dataset.variantId;

    const scope = btn.closest('[data-product-options]') || card;
    const optionSelects = [...scope.querySelectorAll('[data-product-option-select]')];
    const selectedOptions = optionSelects.map(select => select.value).filter(Boolean);
    const variantsJson = card.querySelector('[data-product-variants-json]');

    if (variantsJson && optionSelects.length > 0 && selectedOptions.length === optionSelects.length) {
      try {
        const variants = JSON.parse(variantsJson.textContent);
        const selectedVariant = variants.find(variant => {
          if (!variant.available) return false;
          return selectedOptions.every((value, index) => variant.options?.[index] === value);
        });
        if (selectedVariant?.id) return selectedVariant.id;
      } catch (error) {
        console.error('[ProductManager] Invalid variants JSON:', error);
      }
    }

    const variantSelect = scope.querySelector('[data-product-variant-select]');
    return variantSelect?.value || btn.dataset.variantId;
  }

  // Resolve a variante a partir da cor (swatch) selecionada no card.
  _resolveColorVariant(btn) {
    const card = btn.closest('.productCard');
    if (!card) return null;

    const swatch = card.querySelector('[data-color-swatch][data-selected="true"]')
      || card.querySelector('[data-color-swatch]');
    if (!swatch) return null;

    const colorValue = swatch.dataset.colorValue;
    const position = parseInt(swatch.dataset.colorPosition, 10) - 1;
    const variantsJson = card.querySelector('[data-product-variants-json]');
    if (!colorValue || Number.isNaN(position) || !variantsJson) return null;

    try {
      const variants = JSON.parse(variantsJson.textContent);
      const match = variants.find(variant => {
        if (!variant.available || !variant.options) return false;
        return variant.options[position] === colorValue;
      });
      if (match?.id) return match.id;
    } catch (error) {
      console.error('[ProductManager] Invalid variants JSON:', error);
    }
    return null;
  }

  async _addToCart(btn, variantId) {
    // Congela as dimensões do botão para evitar layout shift ao injetar SVG
    btn.style.width  = `${btn.offsetWidth}px`;
    btn.style.height = `${btn.offsetHeight}px`;

    const result = await window.CartManager?.add(variantId, { button: btn });

    btn.style.width  = '';
    btn.style.height = '';

    if (result?.success) {
      const card = btn.closest('.productCard');
      if (card) this._closePopup(card);
    }
  }

  destroy() {
    document.removeEventListener('click', this._onClick);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.ProductManager = new ProductManager();
});
